/**
 * 营养工具函数
 * 职责：营养相关计算、转换、验证等工具函数
 */

import { 
  NRV_REFERENCE, 
  STANDARD_UNITS, 
  getStandardUnit, 
  getNRVReference,
  getStandardUnitFromAlias,
  convertUnit,
  getRoundingRule
} from '../config/nutrition.config';

/**
 * 计算NRV%（营养素参考值百分比）
 * @param {string} nutrientName - 营养素名称
 * @param {string|number} per100g - 每100克含量（标准单位）
 * @param {string} unit - 单位（可选，用于验证）
 * @returns {string} NRV%值，如 "21%" 或 "%"（如果无法计算）
 */
export function calculateNRV(nutrientName, per100g, unit = null) {
  // 如果值为空或0，返回占位符
  if (!per100g || per100g === '0' || per100g === '' || per100g === null || per100g === undefined) {
    return '%';
  }

  // 获取NRV参考值
  const nrvValue = getNRVReference(nutrientName);
  if (!nrvValue) {
    return '%'; // 如果该营养素没有NRV参考值，返回占位符
  }

  // 转换为数字
  const value = parseFloat(per100g);
  if (isNaN(value) || value < 0) {
    return '%';
  }

  // 计算百分比
  const percentage = value / nrvValue * 100;
  
  // 处理小于0.1%的情况（GB 28050标准：小于0.1%可标注为0%）
  if (percentage < 0.1) {
    return '0%';
  }

  // 应用修约规则：NRV%修约规则（通常保留整数或一位小数）
  // 根据GB 28050，NRV%通常修约到整数，但也可以保留一位小数
  const roundedPercentage = Math.round(percentage * 10) / 10;
  
  // 如果修约后小于0.1，返回0%
  if (roundedPercentage < 0.1) {
    return '0%';
  }

  // 格式化输出：如果修约后是整数，不显示小数
  if (roundedPercentage % 1 === 0) {
    return `${Math.round(roundedPercentage)}%`;
  }
  
  return `${roundedPercentage.toFixed(1)}%`;
}

/**
 * 验证营养素单位是否标准
 * @param {string} nutrientName - 营养素名称
 * @param {string} unit - 单位
 * @returns {boolean} 是否标准单位
 */
export function validateNutrientUnit(nutrientName, unit) {
  const standardUnit = getStandardUnit(nutrientName);
  const normalizedUnit = getStandardUnitFromAlias(unit) || unit;
  return standardUnit === normalizedUnit;
}

/**
 * 自动匹配并转换单位
 * @param {string} unit - 输入的单位（可能是别名）
 * @param {string} nutrientName - 营养素名称
 * @returns {string} 标准单位名称
 */
export function autoMatchUnit(unit, nutrientName) {
  if (!unit) {
    return getStandardUnit(nutrientName);
  }
  
  // 尝试通过别名匹配
  const matchedUnit = getStandardUnitFromAlias(unit);
  if (matchedUnit) {
    return matchedUnit;
  }
  
  // 如果直接匹配标准单位，返回标准单位
  const standardUnit = getStandardUnit(nutrientName);
  if (unit === standardUnit) {
    return standardUnit;
  }
  
  // 如果无法匹配，返回标准单位（用户需要手动确认）
  return standardUnit;
}

/**
 * 转换单位并重新计算数值
 * @param {string|number} value - 原始数值
 * @param {string} fromUnit - 源单位
 * @param {string} toUnit - 目标单位
 * @param {string} nutrientName - 营养素名称（用于修约）
 * @returns {string} 转换并修约后的数值字符串
 */
export function convertAndRound(value, fromUnit, toUnit, nutrientName) {
  if (!value || value === '0' || value === '') {
    return '0';
  }
  
  // 尝试转换单位
  const convertedValue = convertUnit(value, fromUnit, toUnit);
  
  if (convertedValue === null) {
    // 无法转换，返回原值（但可能需要修约）
    return roundNutrientValue(value, nutrientName);
  }
  
  // 转换成功，应用修约规则
  return roundNutrientValue(convertedValue, nutrientName);
}

/**
 * 转换能量单位（kcal转kJ）
 * @param {number} kcal - 千卡值
 * @returns {number} 千焦值
 */
export function convertKcalToKj(kcal) {
  // 1 kcal = 4.184 kJ
  return kcal * 4.184;
}

/**
 * 转换能量单位（kJ转kcal）
 * @param {number} kj - 千焦值
 * @returns {number} 千卡值
 */
export function convertKjToKcal(kj) {
  // 1 kJ = 0.239 kcal
  return kj * 0.239;
}

/**
 * 根据GB 28050标准修约数值
 * @param {string|number} value - 原始数值
 * @param {string} nutrientName - 营养素名称
 * @returns {string} 修约后的数值字符串
 */
export function roundNutrientValue(value, nutrientName) {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  const num = parseFloat(value);
  if (isNaN(num) || num < 0) {
    return '0';
  }

  // 小于0.1的值可以标注为0（GB 28050标准）
  if (num < 0.1) {
    return '0';
  }

  // 获取该营养素的修约规则
  const rule = getRoundingRule(nutrientName);
  
  if (rule.method === 'round') {
    // 直接修约到指定小数位数
    const rounded = Math.round(num * Math.pow(10, rule.decimals)) / Math.pow(10, rule.decimals);
    return rounded.toFixed(rule.decimals);
  } else if (rule.method === 'conditional' && rule.threshold !== undefined) {
    // 条件修约：≥threshold时修约到个位数，<threshold时修约到指定小数位数
    if (num >= rule.threshold) {
      // ≥threshold：修约到个位数
      return Math.round(num).toString();
    } else {
      // <threshold：修约到指定小数位数
      const rounded = Math.round(num * Math.pow(10, rule.decimals)) / Math.pow(10, rule.decimals);
      return rounded.toFixed(rule.decimals);
    }
  } else {
    // 默认：保留一位小数
    return Math.round(num * 10) / 10;
  }
}

/**
 * 格式化营养素数值（兼容旧接口）
 * @param {string|number} value - 数值
 * @param {number} decimals - 小数位数（默认1位）
 * @param {string} nutrientName - 营养素名称（可选，如果提供则使用GB 28050修约规则）
 * @returns {string} 格式化后的数值
 */
export function formatNutrientValue(value, decimals = 1, nutrientName = null) {
  // 如果提供了营养素名称，使用GB 28050修约规则
  if (nutrientName) {
    return roundNutrientValue(value, nutrientName);
  }
  
  // 否则使用旧的格式化方法
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  const num = parseFloat(value);
  if (isNaN(num)) {
    return '0';
  }

  // 根据GB 28050标准：
  // - 小于0.1的值可以标注为0或"未检出"
  // - 大于等于0.1但小于1的值，保留一位小数
  // - 大于等于1的值，保留整数或一位小数
  
  if (num < 0.1) {
    return '0';
  }

  return num.toFixed(decimals);
}

/**
 * 验证营养数据格式
 * @param {Object} nutritionItem - 营养项数据
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateNutritionItem(nutritionItem) {
  const errors = [];

  // 验证营养素名称
  if (!nutritionItem.name || nutritionItem.name.trim() === '') {
    errors.push('营养素名称不能为空');
  }

  // 验证每100克含量
  if (nutritionItem.per100g === undefined || nutritionItem.per100g === null) {
    errors.push('每100克含量不能为空');
  } else {
    const value = parseFloat(nutritionItem.per100g);
    if (isNaN(value) || value < 0) {
      errors.push('每100克含量必须为非负数');
    }
  }

  // 验证单位
  if (!nutritionItem.unit || nutritionItem.unit.trim() === '') {
    errors.push('单位不能为空');
  } else {
    // 验证单位是否标准
    const standardUnit = getStandardUnit(nutritionItem.name);
    if (standardUnit && nutritionItem.unit !== standardUnit) {
      // 警告：单位不符合标准，但不阻止保存
      console.warn(`营养素"${nutritionItem.name}"的单位"${nutritionItem.unit}"不符合标准单位"${standardUnit}"`);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * 验证营养成分表数据的完整性
 * @param {Object} nutritionData - 营养成分表数据 { items: [] }
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[] }
 */
export function validateNutritionTable(nutritionData) {
  const errors = [];
  const warnings = [];

  if (!nutritionData || !nutritionData.items) {
    errors.push('营养成分数据不能为空');
    return { valid: false, errors, warnings };
  }

  const items = nutritionData.items;
  
  if (!Array.isArray(items) || items.length === 0) {
    errors.push('至少需要添加一个营养素');
    return { valid: false, errors, warnings };
  }

  // 验证每个营养项
  items.forEach((item, index) => {
    const itemValidation = validateNutritionItem(item);
    if (!itemValidation.valid) {
      itemValidation.errors.forEach(error => {
        errors.push(`第${index + 1}行：${error}`);
      });
    }

    // 验证NRV%格式
    if (item.nrv && item.nrv !== '%') {
      const nrvMatch = item.nrv.match(/^(\d+(\.\d+)?)%$/);
      if (!nrvMatch) {
        warnings.push(`第${index + 1}行：NRV%格式可能不正确，应为数字加%号（如：21%）`);
      }
    }

    // 验证数值是否为0或空的情况
    if (item.per100g === '0' || item.per100g === '' || !item.per100g) {
      warnings.push(`第${index + 1}行：营养素"${item.name}"的每100克含量为0，请确认是否正确`);
    }
  });

  // 检查必填营养素（GB 28050标准）- 只在最后检查一次
  const requiredNutrients = ['能量', '蛋白质', '脂肪', '碳水化合物', '钠'];
  const hasRequiredNutrients = requiredNutrients.every(name => 
    items.some(item => item.name === name)
  );
  
  if (!hasRequiredNutrients) {
    warnings.push('建议包含GB 28050标准要求的必填营养素：能量、蛋白质、脂肪、碳水化合物、钠');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * 从Food实体数据转换为营养标签格式
 * @param {Object} food - Food实体数据
 * @returns {Array} 营养项数组
 */
export function convertFoodToNutritionItems(food) {
  const items = [];

  // 能量（需要从kcal转换为kJ）
  if (food.energy !== null && food.energy !== undefined) {
    const energyKj = convertKcalToKj(food.energy);
    items.push({
      name: '能量',
      per100g: formatNutrientValue(energyKj, 0, '能量'),
      unit: '千焦',
      nrv: calculateNRV('能量', energyKj),
      locked: false
    });
  }

  // 蛋白质
  if (food.protein !== null && food.protein !== undefined) {
    items.push({
      name: '蛋白质',
      per100g: formatNutrientValue(food.protein, 1, '蛋白质'),
      unit: '克',
      nrv: calculateNRV('蛋白质', food.protein),
      locked: false
    });
  }

  // 脂肪
  if (food.fat !== null && food.fat !== undefined) {
    items.push({
      name: '脂肪',
      per100g: formatNutrientValue(food.fat, 1, '脂肪'),
      unit: '克',
      nrv: calculateNRV('脂肪', food.fat),
      locked: false
    });
  }

  // 碳水化合物
  if (food.carbs !== null && food.carbs !== undefined) {
    items.push({
      name: '碳水化合物',
      per100g: formatNutrientValue(food.carbs, 1, '碳水化合物'),
      unit: '克',
      nrv: calculateNRV('碳水化合物', food.carbs),
      locked: false
    });
  }

  // 膳食纤维
  if (food.fiber !== null && food.fiber !== undefined) {
    items.push({
      name: '膳食纤维',
      per100g: formatNutrientValue(food.fiber, 1, '膳食纤维'),
      unit: '克',
      nrv: calculateNRV('膳食纤维', food.fiber),
      locked: false
    });
  }

  return items;
}

/**
 * 生成默认营养素列表（基于GB 28050必填项）
 * @returns {Array} 默认营养素项数组
 */
export function generateDefaultNutritionItems() {
  const requiredNutrients = ['能量', '蛋白质', '脂肪', '碳水化合物', '钠'];
  
  return requiredNutrients.map(name => ({
    name,
    per100g: '0',
    unit: getStandardUnit(name),
    nrv: '%',
    locked: false
  }));
}

export default {
  calculateNRV,
  validateNutrientUnit,
  convertKcalToKj,
  convertKjToKcal,
  formatNutrientValue,
  roundNutrientValue,
  validateNutritionItem,
  validateNutritionTable,
  convertFoodToNutritionItems,
  generateDefaultNutritionItems,
  autoMatchUnit,
  convertAndRound
};

